// askSmart-ui/src/pages/TOSP/components/BudgetPublishModal.tsx
import React, { useEffect, useState } from "react";

const money = (value: number): string =>
  value.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  });

interface BudgetPublishModalProps {
  mode: "publish" | "unlock" | null;
  /** The budget's name, so the dialog names what it is about to act on. */
  label: string;
  /** Headline figures, shown on publish so the admin confirms what is freezing. */
  summary?: { revenue: number; expenses: number; recovered: number; statements: number };
  /** When the figures now on screen were published, shown on unlock. */
  publishedAt?: string;
  busy?: boolean;
  error?: string | null;
  onCancel: () => void;
  onPublish: () => void;
  onUnlock: (reason: string) => void;
}

/**
 * The two ends of publication, in one dialog because they are one decision read
 * from either side: freezing the budget, and reopening it.
 *
 * Publishing shows the figures that are about to stop moving. Unlocking asks
 * why, and will not proceed without an answer — the server refuses a blank
 * reason too, so this is the courtesy and not the control.
 *
 * Built on the house modal classes rather than its own, so it matches the Add
 * Budget and history dialogs it sits beside.
 */
export default function BudgetPublishModal({
  mode,
  label,
  summary,
  publishedAt,
  busy = false,
  error = null,
  onCancel,
  onPublish,
  onUnlock,
}: BudgetPublishModalProps) {
  const [reason, setReason] = useState("");
  // Cleared on open rather than on close, so a reason typed and cancelled is
  // not still sitting there the next time somebody opens it.
  useEffect(() => {
    if (mode) setReason("");
  }, [mode]);

  if (!mode) return null;

  const publishing = mode === "publish";
  const canSubmit = publishing ? !busy : !busy && reason.trim().length > 0;

  return (
    <div className="smrtdocs-modal-overlay">
      <div className="smrtdocs-modal" style={{ width: 480 }} role="dialog" aria-modal="true">
        <div className="smrtdocs-modal-header">
          <h2 style={{ margin: 0, fontSize: "1rem" }}>
            {publishing ? `Publish ${label}` : `Unlock ${label}`}
          </h2>
        </div>

        <div className="smrtdocs-modal-body">
          {publishing ? (
            <>
              <p style={{ marginTop: 0 }}>
                This records that the final numbers have gone to QuickBooks and
                freezes the whole financial state — revenue, the CAM recovery,
                the management fee, the expense allocations and every manual
                adjustment. Later changes to leases, rates or configuration will
                not move it.
              </p>
              {summary && (
                <div className="tosp-field" style={{ marginBottom: 16 }}>
                  {(
                    [
                      ["Revenue", money(summary.revenue)],
                      ["Expenses", money(summary.expenses)],
                      ["Recovered as CAMs", money(summary.recovered)],
                      ["Tenant statements", String(summary.statements)],
                    ] as [string, string][]
                  ).map(([name, value]) => (
                    <div
                      key={name}
                      style={{ display: "flex", justifyContent: "space-between", gap: 16 }}
                    >
                      <span className="tosp-label" style={{ margin: 0 }}>
                        {name}
                      </span>
                      <span>{value}</span>
                    </div>
                  ))}
                </div>
              )}
              <p className="tosp-field-hint">
                Tenants see a published year on their own Expenses screen only
                while Broadcast Expenses is also on, which stays your decision
                afterwards.
              </p>
            </>
          ) : (
            <>
              <p style={{ marginTop: 0 }}>
                <strong>This may change the numbers.</strong> Unlocking returns
                the budget to live calculation, so the figures will follow any
                lease, rate or configuration change made since it was published
                {publishedAt
                  ? ` on ${new Date(publishedAt).toLocaleDateString()}`
                  : ""}
                . Tenants lose access to the year immediately and get it back
                when you publish again.
              </p>
              <div className="tosp-field" style={{ marginBottom: 16 }}>
                <label className="tosp-label" htmlFor="tosp-unlock-reason">
                  Reason for unlocking <span className="tosp-required">*</span>
                </label>
                <textarea
                  id="tosp-unlock-reason"
                  className="manage-input"
                  rows={3}
                  value={reason}
                  autoFocus
                  placeholder="e.g. Columbia's 2025 corporate-store expenses were missing"
                  onChange={(e) => setReason(e.target.value)}
                />
                <p className="tosp-field-hint">
                  Recorded in the Audit log against this budget, with your name,
                  the time and the address you are working from. The figures
                  already published are kept until you republish, so nothing is
                  lost by reopening it.
                </p>
              </div>
            </>
          )}

          {error && (
            <p className="tosp-field-error" role="alert">
              {error}
            </p>
          )}

          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <button type="button" className="btn-cancel" onClick={onCancel} disabled={busy}>
              Cancel
            </button>
            <button
              type="button"
              className="btn-save"
              disabled={!canSubmit}
              title={canSubmit || publishing ? undefined : "A reason is required"}
              onClick={() => (publishing ? onPublish() : onUnlock(reason.trim()))}
            >
              {busy
                ? publishing
                  ? "Publishing…"
                  : "Unlocking…"
                : publishing
                  ? "Publish Budget"
                  : "Unlock Budget"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
